'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import Link from 'next/link';
import { HeroDashboard } from './HeroDashboard';
import { HeroPhoneScreen } from './HeroPhone';
import { primaryButton, secondaryButton } from '../WebsiteHero';
import { LAYOUT, T, W } from '../tokens';
import {
  FRAME_H, FRAME_W, PHONE_BEZEL, PHONE_H, PHONE_W, SCREEN_K,
  cameraX, computeLayout, frameToOuter, offsetCentre, screenToOuter,
  type HeroLayout, type Point,
} from '@/lib/website-hero/geometry';
import { LOOP_MS, STEPS, stateAfter, stepIndexAt, type CursorTarget } from '@/lib/website-hero/timeline';

/**
 * The home page hero (handoff-hero README): the headline, and under it a live
 * product frame that plays one entry from start to finish on a loop.
 *
 * Everything in the frame is real DOM, not a video: crisp at any size, and the
 * copy and figures are editable. It is decorative, so it is aria-hidden and
 * inert; the call to action is the two buttons above it.
 *
 * One clock drives the loop (see timeline.ts). It pauses when the hero is
 * off-screen or the tab is hidden, and restarts from the beginning when it
 * comes back, so a visitor never lands mid-story.
 */

export const HERO_BG = '#0A1311';
/** The subline, a step lighter than the page's usual mute so it holds up on the glow. */
const SUBLINE = '#B8C9C4';
const ENTRANCE_DELAY_MS = 80;
const GLOW_MS = 8000;

/* The three places each glow drifts between, every 8 seconds. */
const GLOW_1: Array<[number, number, number]> = [[0, 0, 1], [-90, 34, 1.08], [84, -22, 0.95]];
const GLOW_2: Array<[number, number]> = [[0, 0], [130, -34], [-120, 24]];

/** Where the cursor idles before it has anywhere to be, in frame units. */
const CURSOR_REST: Point = { x: 1000, y: 430 };

const PHONE_TARGETS: ReadonlySet<CursorTarget> = new Set(['getTickets', 'pairs', 'continue', 'applePay', 'addWallet', 'passAdd', 'notification']);

/** Once the loop reaches the phone, the story card and email start loading. */
const PHONE_STEP = STEPS.findIndex(([, p]) => p.phone === true) + 1;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function subscribeReducedMotion(onChange: () => void) {
  const mq = window.matchMedia(REDUCED_MOTION);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION).matches,
    // The server can't know; render the animated version and let the client
    // settle it before anything has had time to move.
    () => false,
  );
}

export function HomeHero() {
  const reduceMotion = usePrefersReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);

  const cursorRef = useRef<HTMLDivElement>(null);

  const [enteredAfterDelay, setEnteredAfterDelay] = useState(false);
  const [stepCount, setStepCount] = useState(0);
  // Latches: the heavy phone content (the rendered story card, the email)
  // loads once the phone has first been seen, and then stays loaded.
  const [warm, setWarm] = useState(false);
  const [glow, setGlow] = useState(0);
  const [visible, setVisible] = useState(false);
  const [outerWidth, setOuterWidth] = useState(0);

  // Reduced motion: everything in its final place at once, Home as a still,
  // no loop, no drift.
  const entered = reduceMotion || enteredAfterDelay;
  const running = visible && !reduceMotion;

  const layout = useMemo(() => computeLayout(outerWidth), [outerWidth]);
  const state = useMemo(() => stateAfter(stepCount), [stepCount]);

  /* ── Size. The first reading can be 0 before layout settles, and a 0 scale
     draws a blank frame, so measure on resize, again after 300ms, and fall
     back to a sane width until a real one arrives (computeLayout does). ── */
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const measure = () => { if (el.clientWidth > 0) setOuterWidth(el.clientWidth); };
    // The observer reports the initial size itself, so no direct call here.
    const late = window.setTimeout(measure, 300);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => { window.clearTimeout(late); ro.disconnect(); };
  }, []);

  /* ── Entrance ── */
  useEffect(() => {
    const id = window.setTimeout(() => setEnteredAfterDelay(true), ENTRANCE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  /* ── Run only while the hero is on screen and the tab is visible ── */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    let onScreen = false;
    const update = () => setVisible(onScreen && document.visibilityState === 'visible');
    const io = new IntersectionObserver(([entry]) => { onScreen = entry.isIntersecting; update(); }, { threshold: 0.05 });
    io.observe(section);
    document.addEventListener('visibilitychange', update);
    return () => { io.disconnect(); document.removeEventListener('visibilitychange', update); };
  }, []);

  /* ── The loop's clock. Restarts from zero every time it resumes. ── */
  useEffect(() => {
    if (!running) return;
    let start = performance.now();
    let raf = 0;
    let last = -1;
    const tick = (now: number) => {
      let elapsed = now - start;
      if (elapsed >= LOOP_MS) {
        start += Math.floor(elapsed / LOOP_MS) * LOOP_MS;
        elapsed = now - start;
      }
      const n = stepIndexAt(elapsed);
      if (n !== last) {
        last = n;
        setStepCount(n);
        if (n >= PHONE_STEP) setWarm(true);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    // Paused: back to the top, so it comes back at the start of the story.
    return () => { cancelAnimationFrame(raf); setStepCount(0); };
  }, [running]);

  /* ── Glow drift ── */
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setGlow((g) => (g + 1) % 3), GLOW_MS);
    return () => window.clearInterval(id);
  }, [running]);

  /* ── Cursor. Aims at where the target element is laid out, not at a
     hand-typed coordinate, so a copy change can't leave it tapping air. ── */
  const aim = useCallback((target: CursorTarget, at: HeroLayout): Point => {
    if (target === 'rest') return frameToOuter(CURSOR_REST, at, state.camera);
    const phone = PHONE_TARGETS.has(target);
    // The screen's own root is the positioned element offsets resolve to; the
    // scaling wrapper around it is transformed but not positioned, so it is
    // never an offsetParent.
    const root = phone ? (screenRef.current?.firstElementChild ?? null) : frameRef.current;
    const el = root?.querySelector<HTMLElement>(`[data-hero-target="${target}"]`) ?? null;
    const p = offsetCentre(el, root);
    if (!p) return frameToOuter(CURSOR_REST, at, state.camera);
    return phone ? screenToOuter(p, at) : frameToOuter(p, at, state.camera);
  }, [state.camera]);

  const { mobile, scale } = layout;
  const camX = cameraX(layout, state.camera);
  const cursorSize = mobile ? 26 : Math.max(18, 34 * scale);

  // Written straight to the node: it depends on measured layout, and moving it
  // through state would cost a second render for every cursor move. The CSS
  // transition on left/top does the travel.
  useLayoutEffect(() => {
    const el = cursorRef.current;
    if (!el) return;
    const at = aim(state.cursor, layout);
    // transform, not left/top: it moves on the compositor, so a busy main
    // thread (the story card rendering, on a slow phone) can't make it stutter.
    el.style.transform = `translate(${at.x - cursorSize * 0.15}px, ${at.y - cursorSize * 0.1}px)`;
  }, [aim, state.cursor, state.screen, state.doneScroll, state.pass, state.notification, layout, cursorSize]);
  const g1 = GLOW_1[glow];
  const g2 = GLOW_2[glow];
  const shown = entered && state.body;

  return (
    <section ref={sectionRef} className="fvh" style={{ position: 'relative', color: '#F2F6F5' }}>
      <style>{HERO_CSS}</style>

      {/* Glows. They start above the section so the light washes up behind the
          header rather than stopping in a hard line under it. */}
      <div aria-hidden style={{ position: 'absolute', left: 0, right: 0, top: -90, bottom: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: '50%', top: -90, width: 1180, height: 760, marginLeft: -590, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(42,223,223,.15), rgba(42,223,223,0))', transform: `translate(${g1[0]}px, ${g1[1]}px) scale(${g1[2]})`, transition: 'transform 8s ease-in-out' }} />
        <div style={{ position: 'absolute', left: '50%', top: 350, width: 820, height: 560, marginLeft: -410, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(0,109,109,.34), rgba(0,109,109,0))', transform: `translate(${g2[0]}px, ${g2[1]}px)`, transition: 'transform 10s ease-in-out' }} />
        <div style={{ position: 'absolute', left: '50%', top: 690, width: 1560, height: 1000, marginLeft: -780, borderRadius: '50%', border: '1px solid rgba(42,223,223,.09)' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 200, background: `linear-gradient(rgba(10,19,17,0), ${HERO_BG})` }} />
      </div>

      {/* Copy: the site's own headline block, with a rise-in */}
      <div
        className="fvw-hero"
        style={{ position: 'relative', maxWidth: LAYOUT.maxWidth, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 25, padding: '92px 64px 56px' }}
      >
        <h1 style={{ ...T.heroH1, color: W.bone, margin: 0, maxWidth: 1020, ...rise(entered, 0) }} className="fvw-h1">
          The platform fitness events{' '}
          <span className="fvh-h1-tail" style={{ color: W.ice }}>actually run on.</span>
        </h1>
        <p style={{ ...T.heroSub, color: SUBLINE, margin: 0, maxWidth: 720, ...rise(entered, 0.12) }} className="fvw-herosub">
          Sell the entries, run the day and close the books, on your own brand, from the page to the inbox.
        </p>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', ...rise(entered, 0.22) }}>
          <Link href="/sign-up" style={primaryButton} className="fvw-cta">Get started</Link>
          <a href="#walkthrough" style={secondaryButton} className="fvw-cta-secondary">Book a walkthrough</a>
        </div>
      </div>

      {/* The product frame */}
      <div className="fvh-stage" style={{ position: 'relative', maxWidth: 1120, margin: '0 auto', perspective: 1800 }}>
        <div
          ref={outerRef}
          className="fvh-frame"
          aria-hidden
          inert
          style={{
            position: 'relative', maxWidth: 1100, margin: '0 auto', height: layout.height, overflow: 'hidden',
            borderRadius: mobile ? 18 : Math.max(8, Math.round(16 * scale * 1.2)),
            background: '#FFFFFF',
            boxShadow: '0 0 0 1px rgba(255,255,255,.06), 0 40px 90px rgba(0,0,0,.5)',
            transformOrigin: '50% 0',
            opacity: shown ? 1 : 0,
            transform: entered ? 'translateY(0) rotateX(0deg)' : 'translateY(44px) rotateX(10deg)',
            transition: 'opacity .45s ease, transform 1.3s cubic-bezier(.2,.7,.2,1) .3s',
          }}
        >
          <div
            ref={frameRef}
            style={{
              position: 'absolute', left: 0, top: 0, width: FRAME_W, height: FRAME_H, transformOrigin: '0 0',
              transform: `scale(${scale}) translateX(${-camX}px)`,
              transition: state.body ? 'transform 1s cubic-bezier(.4,0,.2,1)' : 'none',
            }}
          >
            <HeroDashboard state={state} />
          </div>

          {/* Scrim behind the phone */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,19,17,.34)', opacity: state.phone ? 1 : 0, transition: 'opacity .35s ease', pointerEvents: 'none' }} />

          {/* The athlete's phone */}
          <div style={{ position: 'absolute', left: layout.phone.x, top: layout.phone.y, width: PHONE_W, height: PHONE_H, transformOrigin: '0 0', transform: `scale(${layout.phone.scale})`, pointerEvents: 'none' }}>
            <div
              style={{
                width: PHONE_W, height: PHONE_H, boxSizing: 'border-box', padding: PHONE_BEZEL, borderRadius: 44, background: '#0C1512',
                boxShadow: '0 0 0 1px rgba(255,255,255,.08), 0 34px 80px rgba(0,0,0,.42)',
                transformOrigin: mobile ? '50% 0' : '100% 0',
                opacity: state.phone ? 1 : 0,
                transform: state.phone ? 'translateY(0) scale(1)' : mobile ? 'translateY(40px) scale(.95)' : 'translateY(-24px) scale(.96)',
                transition: 'opacity .3s ease, transform .5s cubic-bezier(.2,.7,.2,1)',
              }}
            >
              <div style={{ width: PHONE_W - 2 * PHONE_BEZEL, height: PHONE_H - 2 * PHONE_BEZEL, borderRadius: 35, overflow: 'hidden' }}>
                <div ref={screenRef} style={{ transformOrigin: '0 0', transform: `scale(${SCREEN_K})` }}>
                  <HeroPhoneScreen
                    screen={state.screen}
                    pairsSelected={state.pairsSelected}
                    press={state.press}
                    applePay={state.applePay}
                    doneScroll={state.doneScroll}
                    pass={state.pass}
                    notification={state.notification}
                    email={state.email}
                    emailScrolled={state.emailScrolled}
                    warm={warm}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Cursor and tap ripple. The arrow's tip is at 15% / 10% of its box. */}
          <div
            ref={cursorRef}
            data-hero-cursor
            style={{
              position: 'absolute', left: 0, top: 0, width: cursorSize, height: cursorSize,
              zIndex: 9, pointerEvents: 'none', opacity: state.cursorOn ? 1 : 0, willChange: 'transform',
              transition: 'transform .7s cubic-bezier(.4,0,.2,1), opacity .3s ease',
            }}
          >
            <span
              style={{
                position: 'absolute', left: cursorSize * 0.15 - cursorSize * 0.53, top: cursorSize * 0.1 - cursorSize * 0.53,
                width: cursorSize * 1.06, height: cursorSize * 1.06, borderRadius: '50%', background: 'rgba(0,109,109,.26)',
                opacity: state.ripple ? 1 : 0, transform: `scale(${state.ripple ? 1 : 0.3})`, transition: 'opacity .3s ease, transform .35s ease',
              }}
            />
            <svg width={cursorSize} height={cursorSize} viewBox="0 0 20 20" style={{ position: 'relative', display: 'block', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,.25))' }}>
              <path d="M3 2l13 6.5-5.6 1.7L7.8 15.6Z" fill="#0C1512" stroke="#FFFFFF" strokeWidth="1.4" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}

/** The copy's rise-in: opacity then transform, each element a little later. */
function rise(entered: boolean, delay: number): React.CSSProperties {
  return {
    opacity: entered ? 1 : 0,
    transform: entered ? 'translateY(0)' : 'translateY(16px)',
    transition: `opacity .9s ease ${delay}s, transform 1s cubic-bezier(.2,.7,.2,1) ${delay}s`,
  };
}

/* The stage's gutters step down with the viewport, and a few loops and the
   reduced-motion switch need real CSS. The headline's own responsive sizes
   live in globals.css (.fvw-hero, .fvw-h1, .fvw-herosub). */
const HERO_CSS = `
.fvh-stage { padding: 0 40px 76px; }
.fvh-caret { animation: fvhBlink 1s steps(1) infinite; }
@keyframes fvhBlink { 50% { visibility: hidden; } }
.fvh-faceid { animation: fvhFaceId 1.1s ease-in-out infinite; }
@keyframes fvhFaceId { 50% { transform: scale(.9); opacity: .7; } }
@media (max-width: 700px) {
  .fvh-stage { padding: 0 20px 56px; }
}
/* On a phone the headline wraps to three lines; balance them, and keep
   "actually run on." together, so no line is left holding one word. */
@media (max-width: 760px) {
  .fvw-h1, .fvw-herosub { text-wrap: balance; }
  .fvh-h1-tail { white-space: nowrap; }
}
@media (prefers-reduced-motion: reduce) {
  .fvh *, .fvh *::before, .fvh *::after { transition: none !important; animation: none !important; }
}
`;
